<template>
  <div>
    <v-container fluid>
      <v-skeleton-loader
        :loading="!flagNav"
        transition="scale-transition"
        max-height="64"
        type="table-heading"
      >
        <v-toolbar color="primary" class="header-title text-white">
          <v-toolbar-title class="title">{{ $t("_CommunityManagement") }}</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <v-card class="card2">
        <v-card-text>
          <v-row align="center">
            <!-- Search Section -->
            <v-col cols="12" md="6">
              <v-card variant="outlined" class="pa-2 search-panel">
                <v-row no-gutters style="display: flex;align-items: center;gap: 18px;padding: 10px;">
                  <v-text-field
                    label="Community Name"
                    density="comfortable"
                    variant="outlined"
                    prepend-inner-icon="mdi-text-box-search-outline"
                    hide-details
                    clearable
                    v-model="searchName"
                    @keydown.enter="Search"
                    color="primary"
                  ></v-text-field>
                  <v-select
                    label="Status"
                    density="comfortable"
                    variant="outlined"
                    hide-details
                    clearable
                    v-model="searchStatus"
                    :items="statusOptions"
                    style="max-width: 200px;"
                    color="primary"
                  ></v-select>
                  <v-btn
                    color="primary"
                    class="ml-2"
                    variant="elevated"
                    style="min-width: 100px;"
                    @click="Search"
                  >
                    <v-icon start>mdi-magnify</v-icon>
                    Search
                  </v-btn>
                </v-row>
              </v-card>
            </v-col>

            <!-- Buttons -->
            <v-col cols="12" md="6" class="d-flex flex-wrap justify-end gap-2">
              <v-btn
                color="secondary"
                class="control-btn"
                variant="outlined"
                size="large"
                prepend-icon="mdi-refresh"
                @click="fetchCommunities"
                :loading="loading"
                rounded="lg"
              >
                REFRESH
                <v-tooltip activator="parent" location="top">
                  Refresh community list
                </v-tooltip>
              </v-btn>
              <v-btn
                v-if="canAdd"
                color="primary"
                class="control-btn create-btn"
                variant="flat"
                size="large"
                prepend-icon="mdi-plus-circle"
                @click="openCreateDialog"
                rounded="lg"
              >
                CREATE COMMUNITY
                <v-tooltip activator="parent" location="top">
                  Create new Community Detection
                </v-tooltip>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- Data Table -->
        <v-data-table-server
          v-model:options="options"
          :items-per-page="itemsPerPage"
          :items-length="totalItems"
          :items="communities"
          :headers="headers"
          :loading="loading"
        >
          <!-- No Data Template -->
          <template v-slot:no-data>
            <div class="no-data-container">
              <div class="no-data-content">
                <v-icon size="64" color="grey-lighten-2" class="no-data-icon">
                  mdi-graph-outline
                </v-icon>
                <div class="no-data-text">
                  <h3 class="no-data-title">No Communities Found</h3>
                  <p class="no-data-subtitle">Create your first community to detect document clusters.</p>
                </div>
              </div>
            </div>
          </template>

          <template v-slot:item="{ item }">
            <tr>
              <td class="text-center">{{ item.number }}</td>
              <td>
                <div class="d-flex align-center">
                  <v-icon color="primary" class="mr-2">mdi-graph</v-icon>
                  {{ item.communityName }}
                </div>
              </td>
              <td>{{ item.description || '-' }}</td>
              <td>
                <div
                  v-if="item.prompt"
                  class="prompt-cell"
                  @click="showPromptDialog(item)"
                >
                  <span class="prompt-truncate">{{ truncatePrompt(item.prompt, 30) }}</span>
                  <v-icon size="small" class="ml-1">mdi-open-in-new</v-icon>
                  <v-tooltip activator="parent" location="top">Click to view full prompt</v-tooltip>
                </div>
                <span v-else class="text-grey-darken-1">Default</span>
              </td>
              <td class="text-center">
                <v-chip color="primary" variant="tonal" size="small">
                  {{ item.documentCount }} docs
                </v-chip>
              </td>
              <td class="text-center">
                <v-chip
                  :color="getStatusColor(item.status)"
                  variant="flat"
                  size="small"
                  :style="item.status === 'Failed' ? 'cursor: pointer;' : ''"
                  @click="item.status === 'Failed' && item.errorMessage ? showError(item) : null"
                >
                  {{ item.status }}
                  <v-tooltip v-if="item.status === 'Failed' && item.errorMessage" activator="parent" location="top">
                    {{ item.errorMessage }}
                  </v-tooltip>
                </v-chip>
              </td>
              <td class="text-center">
                <v-chip color="secondary" variant="tonal" size="small" v-if="item.groupCount">
                  {{ item.groupCount }} groups
                </v-chip>
                <span v-else>-</span>
              </td>
              <td class="text-center">
                {{ item.modularity ? item.modularity.toFixed(3) : '-' }}
              </td>
              <td class="text-center">{{ item.createBy }}</td>
              <td class="text-center">{{ formatDateTime(item.createDate) }}</td>
              <td class="text-center">
                <div class="action-group">
                  <v-btn
                    variant="text"
                    density="comfortable"
                    @click="openEditDialog(item)"
                    class="action-btn"
                    icon
                  >
                    <v-icon size="18" color="primary">{{ canEdit ? 'mdi-pencil' : 'mdi-eye-outline' }}</v-icon>
                    <v-tooltip activator="parent" location="top">{{ canEdit ? 'Edit Community' : 'View Community' }}</v-tooltip>
                  </v-btn>

                  <v-btn
                    v-if="canEdit"
                    variant="text"
                    density="comfortable"
                    @click="reprocessCommunity(item)"
                    class="action-btn"
                    icon
                    :disabled="item.status === 'Processing'"
                    :loading="item.processing"
                  >
                    <v-icon size="18" color="warning">mdi-refresh</v-icon>
                    <v-tooltip activator="parent" location="top">Reprocess Community</v-tooltip>
                  </v-btn>

                  <v-btn
                    variant="text"
                    density="comfortable"
                    @click="viewGraph(item)"
                    class="action-btn"
                    icon
                    :disabled="item.status !== 'Completed'"
                  >
                    <v-icon size="18" color="info">mdi-chart-bubble</v-icon>
                    <v-tooltip activator="parent" location="top">View Graph</v-tooltip>
                  </v-btn>

                  <v-btn
                    v-if="canDelete"
                    variant="text"
                    density="comfortable"
                    @click="openDeleteDialog(item)"
                    class="action-btn delete-btn"
                    icon
                    :disabled="btnDisableDelete(item)"
                  >
                    <v-icon size="18" :color="btnDisableDelete(item) ? 'grey-lighten-1' : 'error'">mdi-delete-outline</v-icon>
                    <v-tooltip activator="parent" location="top">{{ btnDisableDelete(item) ? 'You can only delete items you created' : 'Delete' }}</v-tooltip>
                  </v-btn>
                </div>
              </td>
            </tr>
          </template>
        </v-data-table-server>
      </v-card>
    </v-container>

    <!-- Create Community Dialog -->
    <v-dialog v-model="createDialog" max-width="800" persistent>
      <v-card>
        <v-card-title class="text-h5 bg-primary text-white pa-4">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          Create Community
        </v-card-title>
        <v-card-text class="pa-4">
          <v-form ref="createForm" v-model="createFormValid">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="createData.communityName"
                  label="Community Name *"
                  variant="outlined"
                  :rules="[v => !!v || 'Name is required']"
                  prepend-inner-icon="mdi-text"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="createData.description"
                  label="Description"
                  variant="outlined"
                  rows="2"
                  prepend-inner-icon="mdi-text-long"
                ></v-textarea>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model.number="createData.resolution"
                  label="Louvain Resolution"
                  variant="outlined"
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="2.0"
                  hint="Louvain resolution parameter (default: 1.0) - Higher values create more communities"
                  persistent-hint
                  prepend-inner-icon="mdi-tune"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <div class="d-flex align-center mb-2">
                  <span class="text-subtitle-1">Entity Extraction Prompt</span>
                  <v-spacer></v-spacer>
                  <v-btn
                    variant="outlined"
                    size="small"
                    color="secondary"
                    @click="loadDefaultPromptForCreate"
                    prepend-icon="mdi-restore"
                  >
                    Default Template
                  </v-btn>
                </div>
                <v-textarea
                  v-model="createData.prompt"
                  label="Custom Prompt (leave empty for default)"
                  variant="outlined"
                  rows="4"
                  prepend-inner-icon="mdi-robot"
                  hint="Customize the prompt used for entity extraction. Leave empty to use default."
                  persistent-hint
                ></v-textarea>
              </v-col>
              <!-- Similarity threshold hidden for EntityGraphRAG mode (entity-based approach uses LLM relations, not similarity) -->
              <v-col cols="12">
                <div class="text-subtitle-1 mb-2">Select Documents *</div>
                <v-text-field
                  v-model="documentSearch"
                  label="Search Documents"
                  variant="outlined"
                  density="compact"
                  prepend-inner-icon="mdi-magnify"
                  hide-details
                  clearable
                  class="mb-2"
                ></v-text-field>
                <v-card variant="outlined" class="pa-2" style="max-height: 300px; overflow-y: auto;">
                  <v-data-table
                    v-model="selectedDocuments"
                    :headers="documentHeaders"
                    :items="availableDocuments"
                    :loading="loadingDocuments"
                    :search="documentSearch"
                    show-select
                    item-value="vectorDbName"
                    return-object
                    density="compact"
                    hide-default-footer
                    :items-per-page="-1"
                  >
                    <template v-slot:item.chunkCount="{ item }">
                      <v-chip color="info" size="x-small" variant="tonal">
                        {{ item.chunkCount }} chunks
                      </v-chip>
                    </template>
                    <template v-slot:item.updateDate="{ item }">
                      {{ formatDateTime(item.updateDate) }}
                    </template>
                  </v-data-table>
                </v-card>
                <div v-if="selectedDocuments.length > 0" class="mt-2 text-caption text-grey">
                  {{ selectedDocuments.length }} document(s) selected
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="createDialog = false">Cancel</v-btn>
          <v-btn
            v-if="canAdd"
            color="primary"
            variant="elevated"
            @click="createCommunity"
            :disabled="!createFormValid || selectedDocuments.length === 0"
            :loading="createLoading"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h5">Delete Community</v-card-title>
        <v-card-text>
          Are you sure you want to delete "{{ deleteItem?.communityName }}"?
          This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn v-if="canDelete" color="error" variant="elevated" @click="deleteCommunity" :loading="deleteLoading">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Community Dialog -->
    <v-dialog v-model="editDialog" max-width="900" persistent>
      <v-card>
        <v-card-title class="text-h5 bg-primary text-white pa-4">
          <v-icon class="mr-2">{{ canEdit ? 'mdi-pencil' : 'mdi-eye-outline' }}</v-icon>
          {{ canEdit ? 'Edit Community' : 'View Community' }}
        </v-card-title>
        <v-card-text class="pa-4">
          <v-form ref="editForm" v-model="editFormValid">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editData.communityName"
                  label="Community Name *"
                  variant="outlined"
                  :rules="[v => !!v || 'Name is required']"
                  prepend-inner-icon="mdi-text"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="editData.description"
                  label="Description"
                  variant="outlined"
                  rows="2"
                  prepend-inner-icon="mdi-text-long"
                ></v-textarea>
              </v-col>
              <v-col cols="12">
                <div class="d-flex align-center mb-2">
                  <span class="text-subtitle-1">Entity Extraction Prompt</span>
                  <v-spacer></v-spacer>
                  <v-btn
                    variant="outlined"
                    size="small"
                    color="secondary"
                    @click="loadDefaultPrompt"
                    prepend-icon="mdi-restore"
                  >
                    Default Template
                  </v-btn>
                </div>
                <v-textarea
                  v-model="editData.prompt"
                  label="Custom Prompt (leave empty for default)"
                  variant="outlined"
                  rows="8"
                  prepend-inner-icon="mdi-robot"
                  hint="Customize the prompt used for entity extraction. Leave empty to use default."
                  persistent-hint
                ></v-textarea>
              </v-col>
              <v-col cols="12">
                <div class="d-flex align-center mb-2">
                  <span class="text-subtitle-1">Select Documents *</span>
                  <v-spacer></v-spacer>
                  <span v-if="editSelectedDocuments.length > 0" class="text-caption text-grey">
                    {{ editSelectedDocuments.length }} document(s) selected
                  </span>
                </div>
                <v-text-field
                  v-model="editDocumentSearch"
                  label="Search Documents"
                  variant="outlined"
                  density="compact"
                  prepend-inner-icon="mdi-magnify"
                  hide-details
                  clearable
                  class="mb-2"
                ></v-text-field>
                <v-card variant="outlined" class="pa-2" style="max-height: 250px; overflow-y: auto;">
                  <v-data-table
                    v-model="editSelectedDocuments"
                    :headers="documentHeaders"
                    :items="availableDocuments"
                    :loading="editLoadingDocuments"
                    :search="editDocumentSearch"
                    show-select
                    item-value="vectorDbName"
                    return-object
                    density="compact"
                    hide-default-footer
                    :items-per-page="-1"
                  >
                    <template v-slot:item.chunkCount="{ item }">
                      <v-chip color="info" size="x-small" variant="tonal">
                        {{ item.chunkCount }} chunks
                      </v-chip>
                    </template>
                    <template v-slot:item.updateDate="{ item }">
                      {{ formatDateTime(item.updateDate) }}
                    </template>
                  </v-data-table>
                </v-card>
                <v-alert
                  v-if="editDocumentsChanged"
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="mt-2"
                >
                  Documents have been changed. Click "Save & Reprocess" to apply changes to the graph.
                </v-alert>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="editDialog = false">{{ isViewOnly ? 'Close' : 'Cancel' }}</v-btn>
          <v-btn
            v-if="canEdit"
            color="secondary"
            variant="elevated"
            @click="saveCommunity(false)"
            :disabled="!editFormValid || editSelectedDocuments.length === 0"
            :loading="editLoading"
          >
            Save
          </v-btn>
          <v-btn
            v-if="canEdit"
            color="primary"
            variant="elevated"
            @click="saveCommunity(true)"
            :disabled="!editFormValid || editSelectedDocuments.length === 0"
            :loading="editLoading"
          >
            Save & Reprocess
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Prompt View Dialog -->
    <v-dialog v-model="promptViewDialog" max-width="800">
      <v-card>
        <v-card-title class="text-h6 bg-grey-lighten-3 pa-4">
          <v-icon class="mr-2">mdi-robot</v-icon>
          Entity Extraction Prompt
        </v-card-title>
        <v-card-text class="pa-4">
          <pre class="prompt-display">{{ promptViewItem?.prompt || 'Using default prompt' }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="promptViewDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading Overlay -->
    <v-overlay v-model="processing" class="align-center justify-center" persistent>
      <v-card class="pa-6 text-center" min-width="300">
        <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
        <div class="mt-4 text-h6">Processing Community Detection...</div>
        <div class="text-caption text-grey">This may take a few minutes</div>
      </v-card>
    </v-overlay>
  </div>
</template>

<script>
export default {
  name: 'CommunityManagement',
  inject: ['Username', 'RoleId'],
  data() {
    return {
      flagNav: true,
      today: new Date().toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      }),

      // Search
      searchName: '',
      searchStatus: null,
      statusOptions: ['Pending', 'Processing', 'Completed', 'Failed'],

      // Table
      loading: false,
      communities: [],
      totalItems: 0,
      itemsPerPage: 10,
      options: {},
      headers: [
        { title: '#', key: 'number', align: 'center', width: '60px', sortable: false },
        { title: 'Name', key: 'communityName', sortable: true },
        { title: 'Description', key: 'description', sortable: false },
        { title: 'Prompt', key: 'prompt', sortable: false, width: '150px' },
        { title: 'Documents', key: 'documentCount', align: 'center', sortable: false },
        { title: 'Status', key: 'status', align: 'center', sortable: true },
        { title: 'Groups', key: 'groupCount', align: 'center', sortable: false },
        { title: 'Modularity', key: 'modularity', align: 'center', sortable: true },
        { title: 'Created By', key: 'createBy', align: 'center', sortable: false },
        { title: 'Created Date', key: 'createDate', align: 'center', sortable: true },
        { title: 'Actions', key: 'actions', align: 'center', width: '160px', sortable: false },
      ],

      // Create Dialog
      createDialog: false,
      createFormValid: false,
      createLoading: false,
      createData: {
        communityName: '',
        description: '',
        algorithm: 'EntityGraphRAG',  // Default and only algorithm
        resolution: 1.0,
        similarityThreshold: 0.7,
        prompt: '',
      },
      selectedDocuments: [],
      availableDocuments: [],
      loadingDocuments: false,
      documentSearch: '',
      documentHeaders: [
        { title: 'Document Name', key: 'vectorDbName', sortable: true },
        { title: 'Chunks', key: 'chunkCount', align: 'center', sortable: true },
        { title: 'Created By', key: 'createBy', align: 'center', sortable: false },
        { title: 'Last Updated', key: 'updateDate', align: 'center', sortable: true },
      ],

      // Delete Dialog
      deleteDialog: false,
      deleteItem: null,
      deleteLoading: false,

      // Edit Dialog
      editDialog: false,
      editFormValid: false,
      editLoading: false,
      editData: {
        id: null,
        communityName: '',
        description: '',
        prompt: '',
      },
      // Edit dialog document selection
      editSelectedDocuments: [],
      editLoadingDocuments: false,
      editDocumentSearch: '',
      originalDocuments: [], // Track for change detection

      // Prompt View Dialog
      promptViewDialog: false,
      promptViewItem: null,

      // Default prompt (cached)
      defaultPrompt: '',

      // Processing
      processing: false,

      // Auto-refresh polling
      pollInterval: null,
    };
  },
  computed: {
    isViewOnly() {
      return this.$pagePermission && this.$pagePermission.isViewOnly('CommunityManagement');
    },
    canAdd() {
      return !!(this.$pagePermission && this.$pagePermission.canAdd('CommunityManagement'));
    },
    canEdit() {
      return !!(this.$pagePermission && this.$pagePermission.canEdit('CommunityManagement'));
    },
    canDelete() {
      return !!(this.$pagePermission && this.$pagePermission.canDelete('CommunityManagement'));
    },
    permissions() {
      return {
        add: this.$pagePermission && this.$pagePermission.canAdd('CommunityManagement'),
        edit: this.$pagePermission && this.$pagePermission.canEdit('CommunityManagement'),
        delete: this.$pagePermission && this.$pagePermission.canDelete('CommunityManagement'),
      }
    },
    editDocumentsChanged() {
      const selectedNames = this.editSelectedDocuments.map(d => d.vectorDbName).sort();
      const originalNames = [...this.originalDocuments].sort();
      if (selectedNames.length !== originalNames.length) return true;
      for (let i = 0; i < selectedNames.length; i++) {
        if (selectedNames[i] !== originalNames[i]) return true;
      }
      return false;
    },
  },
  watch: {
    options: {
      handler() {
        this.fetchCommunities();
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchCommunities();
  },
  beforeUnmount() {
    // Clear polling interval when leaving the page
    this.stopPolling();
  },
  methods: {
    // Mirror the backend permission rule: a "User" (RoleId=2) or "Operator" (RoleId=6)
    // can only delete items they created. Disable the button up-front so they don't
    // get rejected after the confirm dialog.
    btnDisableDelete(item) {
      if ((this.RoleId == 2 || this.RoleId == 6) && item.createBy != this.Username) {
        return true;
      }
      return false;
    },
    fetchCommunities() {
      const self = this;
      self.loading = true;

      const { page, itemsPerPage, sortBy, sortDesc } = self.options;

      const request = {
        page: page || 1,
        perPage: itemsPerPage || 10,
        name: self.searchName || null,
        status: self.searchStatus || null,
        sortBy: sortBy?.[0]?.key || 'createDate',
        sortDesc: sortBy?.[0]?.order === 'desc',
      };

      this.$call.GetTableCommunity(
        request,
        (response) => {
          if (response.status === 0) {
            self.communities = response.data.data || [];
            self.totalItems = response.data.total || 0;
          } else {
            self.communities = [];
            self.totalItems = 0;
          }
          self.loading = false;
        },
        (error) => {
          console.error(error);
          self.loading = false;
          self.$swal.fire('Error', 'Failed to load communities', 'error');
        }
      );
    },

    Search() {
      this.options.page = 1;
      this.fetchCommunities();
    },

    getStatusColor(status) {
      switch (status) {
        case 'Pending': return 'grey';
        case 'Processing': return 'info';
        case 'Completed': return 'success';
        case 'Failed': return 'error';
        default: return 'grey';
      }
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr + 'Z').toLocaleString();
    },

    openCreateDialog() {
      this.createData = {
        communityName: '',
        description: '',
        algorithm: 'EntityGraphRAG',  // Entity-based approach for GraphRAG
        resolution: 1.0,
        similarityThreshold: 0.7,
        prompt: '',
      };
      this.selectedDocuments = [];
      this.createDialog = true;
      this.fetchAvailableDocuments();
    },

    fetchAvailableDocuments() {
      const self = this;
      self.loadingDocuments = true;
      self.editLoadingDocuments = true;

      this.$call.GetAvailableDocuments(
        (response) => {
          if (response.status === 0) {
            self.availableDocuments = response.data || [];
          } else {
            self.availableDocuments = [];
          }
          self.loadingDocuments = false;
          self.editLoadingDocuments = false;
        },
        (error) => {
          console.error(error);
          self.loadingDocuments = false;
          self.editLoadingDocuments = false;
        }
      );
    },

    fetchCommunityDocuments(communityId) {
      const self = this;
      this.$call.GetCommunityById(
        communityId,
        (response) => {
          if (response.status === 0 && response.data) {
            const documentNames = response.data.documents || [];
            self.originalDocuments = [...documentNames];
            // Wait for availableDocuments to be loaded, then select matching ones
            const selectDocs = () => {
              if (self.availableDocuments.length > 0 || !self.editLoadingDocuments) {
                self.editSelectedDocuments = self.availableDocuments.filter(
                  doc => documentNames.includes(doc.vectorDbName)
                );
              } else {
                setTimeout(selectDocs, 100);
              }
            };
            selectDocs();
          }
        },
        (error) => {
          console.error('Failed to fetch community documents:', error);
        }
      );
    },

    createCommunity() {
      const self = this;
      self.createLoading = true;

      const request = {
        communityName: self.createData.communityName,
        description: self.createData.description,
        algorithm: self.createData.algorithm,
        resolution: self.createData.resolution,
        similarityThreshold: self.createData.similarityThreshold,
        vectorDbNames: self.selectedDocuments.map(d => d.vectorDbName),
        prompt: self.createData.prompt || null,
      };

      this.$call.CreateCommunity(
        request,
        (response) => {
          if (response.status === 0) {
            self.$swal.fire('Success', 'Community created. Processing started in background.', 'success');
            self.createDialog = false;
            // Auto-trigger processing in background
            self.startBackgroundProcessing(response.data.id);
            self.fetchCommunities();
          } else {
            self.$swal.fire('Error', response.message || 'Failed to create community', 'error');
          }
          self.createLoading = false;
        },
        (error) => {
          console.error(error);
          self.createLoading = false;
          self.$swal.fire('Error', 'Failed to create community', 'error');
        }
      );
    },

    startBackgroundProcessing(communityId) {
      const self = this;
      // Fire and forget - processing runs in background
      this.$call.ProcessCommunity(
        communityId,
        (response) => {
          if (response.status === 0) {
            // Refresh immediately to show "Processing" status
            self.fetchCommunities();
            // Start polling to check for completion
            self.startPolling();
          }
        },
        (error) => {
          console.error('Background processing error:', error);
          self.fetchCommunities();
        }
      );
    },

    startPolling() {
      // Only start polling if not already polling
      if (this.pollInterval) return;

      // Poll every 5 seconds to check for status updates
      this.pollInterval = setInterval(() => {
        this.checkProcessingStatus();
      }, 5000);
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },

    checkProcessingStatus() {
      const self = this;
      const { page, itemsPerPage, sortBy } = self.options;

      const request = {
        page: page || 1,
        perPage: itemsPerPage || 10,
        name: self.searchName || null,
        status: self.searchStatus || null,
        sortBy: sortBy?.[0]?.key || 'createDate',
        sortDesc: sortBy?.[0]?.order === 'desc',
      };

      this.$call.GetTableCommunity(
        request,
        (response) => {
          if (response.status === 0) {
            const oldCommunities = self.communities;
            self.communities = response.data.data || [];
            self.totalItems = response.data.total || 0;

            // Check if any processing community has completed
            const hasProcessing = self.communities.some(c => c.status === 'Processing');

            // Find communities that just completed
            self.communities.forEach((newItem) => {
              const oldItem = oldCommunities.find(o => o.id === newItem.id);
              if (oldItem && oldItem.status === 'Processing' && newItem.status === 'Completed') {
                self.$swal.fire({
                  toast: true,
                  position: 'top-end',
                  icon: 'success',
                  title: `"${newItem.communityName}" completed!`,
                  showConfirmButton: false,
                  timer: 4000,
                });
              } else if (oldItem && oldItem.status === 'Processing' && newItem.status === 'Failed') {
                self.$swal.fire({
                  icon: 'error',
                  title: `"${newItem.communityName}" failed`,
                  text: newItem.errorMessage || 'Unknown error occurred',
                  confirmButtonText: 'OK',
                });
              }
            });

            // Stop polling if no more processing items
            if (!hasProcessing) {
              self.stopPolling();
            }
          }
        },
        (error) => {
          console.error('Polling error:', error);
        }
      );
    },

    reprocessCommunity(item) {
      const self = this;

      self.$swal.fire({
        title: 'Reprocess Community?',
        text: `This will re-run the community detection on "${item.communityName}". Existing results will be replaced.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Reprocess',
        confirmButtonColor: '#FB8C00',
      }).then((result) => {
        if (result.isConfirmed) {
          // Start processing in background
          self.startBackgroundProcessing(item.id);
          self.$swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'info',
            title: 'Reprocessing started in background',
            showConfirmButton: false,
            timer: 3000,
          });
          self.fetchCommunities();
        }
      });
    },

    showError(item) {
      this.$swal.fire({
        icon: 'error',
        title: `"${item.communityName}" failed`,
        text: item.errorMessage || 'Unknown error occurred',
        confirmButtonText: 'OK',
      });
    },

    viewGraph(item) {
      this.$router.push({ name: 'CommunityGraph', params: { id: item.id } });
    },

    openDeleteDialog(item) {
      this.deleteItem = item;
      this.deleteDialog = true;
    },

    deleteCommunity() {
      const self = this;
      self.deleteLoading = true;

      this.$call.DeleteCommunity(
        self.deleteItem.id,
        (response) => {
          if (response.status === 0) {
            self.$swal.fire('Deleted', 'Community deleted successfully', 'success');
            self.deleteDialog = false;
            self.fetchCommunities();
          } else {
            self.$swal.fire('Error', response.message || 'Failed to delete community', 'error');
          }
          self.deleteLoading = false;
        },
        (error) => {
          console.error(error);
          self.deleteLoading = false;
          self.$swal.fire('Error', 'Failed to delete community', 'error');
        }
      );
    },

    // Prompt-related methods
    truncatePrompt(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

    showPromptDialog(item) {
      this.promptViewItem = item;
      this.promptViewDialog = true;
    },

    openEditDialog(item) {
      this.editData = {
        id: item.id,
        communityName: item.communityName,
        description: item.description,
        prompt: item.prompt || '',
      };
      // Reset document selection state
      this.editSelectedDocuments = [];
      this.editDocumentSearch = '';
      this.originalDocuments = [];
      this.editDialog = true;
      // Fetch available documents and current community documents
      this.fetchAvailableDocuments();
      this.fetchCommunityDocuments(item.id);
    },

    loadDefaultPrompt() {
      const self = this;
      if (this.defaultPrompt) {
        this.editData.prompt = this.defaultPrompt;
        return;
      }

      this.$call.GetDefaultPrompt(
        (response) => {
          if (response.status === 0 && response.data) {
            self.defaultPrompt = response.data.systemPrompt;
            self.editData.prompt = response.data.systemPrompt;
          }
        },
        (error) => {
          console.error('Failed to load default prompt:', error);
          self.$swal.fire('Error', 'Failed to load default prompt', 'error');
        }
      );
    },

    loadDefaultPromptForCreate() {
      const self = this;
      if (this.defaultPrompt) {
        this.createData.prompt = this.defaultPrompt;
        return;
      }

      this.$call.GetDefaultPrompt(
        (response) => {
          if (response.status === 0 && response.data) {
            self.defaultPrompt = response.data.systemPrompt;
            self.createData.prompt = response.data.systemPrompt;
          }
        },
        (error) => {
          console.error('Failed to load default prompt:', error);
          self.$swal.fire('Error', 'Failed to load default prompt', 'error');
        }
      );
    },

    saveCommunity(reprocess) {
      const self = this;
      self.editLoading = true;

      // Get current selected document names
      const selectedNames = self.editSelectedDocuments.map(d => d.vectorDbName);

      // Check if documents changed
      const documentsChanged = !self.arraysEqual(
        selectedNames.sort(),
        [...self.originalDocuments].sort()
      );

      const request = {
        id: self.editData.id,
        communityName: self.editData.communityName,
        description: self.editData.description,
        prompt: self.editData.prompt || null,
        reprocess: reprocess,
        // Only include vectorDbNames if documents changed
        vectorDbNames: documentsChanged ? selectedNames : null,
      };

      this.$call.UpdateCommunity(
        request,
        (response) => {
          if (response.status === 0) {
            const message = reprocess
              ? 'Community saved. Reprocessing started in background.'
              : 'Community saved successfully.';
            self.$swal.fire('Success', message, 'success');
            self.editDialog = false;
            self.fetchCommunities();

            if (reprocess) {
              self.startPolling();
            }
          } else {
            self.$swal.fire('Error', response.message || 'Failed to save community', 'error');
          }
          self.editLoading = false;
        },
        (error) => {
          console.error(error);
          self.editLoading = false;
          self.$swal.fire('Error', 'Failed to save community', 'error');
        }
      );
    },

    arraysEqual(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
      }
      return true;
    },
  },
};
</script>

<style scoped>
.card2 {
  margin-top: 16px;
  padding: 16px;
}

.search-panel {
  background-color: #f5f5f5;
}

.no-data-container {
  padding: 48px;
  text-align: center;
}

.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.no-data-title {
  color: #757575;
  font-weight: 500;
  margin: 0;
}

.no-data-subtitle {
  color: #9e9e9e;
  margin: 0;
}

.action-group {
  display: flex;
  justify-content: center;
  gap: 4px;
}

.action-btn:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.delete-btn:hover {
  color: #f44336 !important;
}

.prompt-cell {
  display: flex;
  align-items: center;
  max-width: 150px;
  cursor: pointer;
}

.prompt-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 120px;
  color: #666;
  font-size: 0.85rem;
}

.prompt-cell:hover .prompt-truncate {
  color: #1976d2;
}

.prompt-display {
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 400px;
  overflow-y: auto;
  font-family: monospace;
  font-size: 0.9rem;
  line-height: 1.5;
}

/* Disabled delete button — clearer visual cue */
.delete-btn.v-btn--disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
}
</style>
